"use client";

import { useEffect } from "react";
import Cookies from "js-cookie";
import { ssoApi } from "@/lib/api";

/**
 * Listens for tab focus + a 5-minute interval to keep the SSO tab aligned
 * with the server-side session state.
 *
 * Why this exists (Decision #1, post-reframe):
 * Logout is per-device, but a single browser may have SSO + Correspondence
 * + Strategy Management open in different tabs. When the user clicks logout
 * in any of them, SSO server-side revokes the shared UserSession. The
 * other tabs in the same browser still hold valid-looking cookies and only
 * discover the revocation on their next API call. This validator forces
 * that discovery on tab focus, so switching back to the SSO tab promptly
 * lands on /login instead of looking authenticated until the user clicks
 * something.
 *
 * Mechanism:
 *   - `ssoApi.silentMe()` returns `null` on definitive 401 without firing
 *     a redirect of its own.
 *   - On `null` we clear cookies and (if not already there) navigate to
 *     `/login`. Middleware handles the rest.
 *   - On success the call refreshes RBAC claims server-side and returns.
 *
 * This mirrors Correspondence's `auth-context.tsx:280-353` pattern.
 */
export function SessionValidator() {
  useEffect(() => {
    const validate = async () => {
      // Only validate when we appear to be authenticated locally.
      // Avoids /me requests on public pages like /login.
      const hasTokens =
        !!Cookies.get("sso_access_token") ||
        !!Cookies.get("sso_refresh_token");
      if (!hasTokens) return;

      const me = await ssoApi.silentMe();
      if (me !== null) return;

      // Server says we're not authenticated. Sync local state + redirect.
      Cookies.remove("sso_access_token", { path: "/" });
      Cookies.remove("sso_refresh_token", { path: "/" });
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem("sso_user");
        } catch {
          // ignore
        }
        const onPublic =
          window.location.pathname === "/" ||
          window.location.pathname.startsWith("/login") ||
          window.location.pathname.startsWith("/logout") ||
          window.location.pathname.startsWith("/auth/");
        if (!onPublic) {
          window.location.href = "/login";
        }
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void validate();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    const intervalId = window.setInterval(() => {
      void validate();
    }, 5 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
}
